import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import NoAccess from "./NoAccess";
import { AccessError, GetUnresolvedEntities } from "../services/ApiService";
import {
  Alert,
  Card,
  Col,
  Row,
  Select,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import { LoadingOutlined, WarningOutlined } from "@ant-design/icons";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
} from "recharts";

const { Text, Title } = Typography;

const COLORS = ["#f5222d", "#fa8c16", "#faad14", "#1677ff", "#722ed1", "#13c2c2"];

const ENTITY_TYPE_COLORS = {};
function colorForType(type) {
  if (!ENTITY_TYPE_COLORS[type]) {
    const idx = Object.keys(ENTITY_TYPE_COLORS).length;
    ENTITY_TYPE_COLORS[type] = COLORS[idx % COLORS.length];
  }
  return ENTITY_TYPE_COLORS[type];
}

function uniqueOptions(data, field) {
  return [...new Set(data.map((d) => d[field]).filter(Boolean))].sort().map((v) => ({ label: v, value: v }));
}

function UnresolvedEntities() {
  const auth = useAuth();
  const token = auth?.user?.id_token;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [filters, setFilters] = useState({ application: [], entity_type: [] });
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    async function fetchData() {
      try {
        const result = await GetUnresolvedEntities(token);
        setData(result);
      } catch (e) {
        if (e instanceof AccessError) setAccessDenied(e.message);
        else setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (accessDenied) return <NoAccess message={accessDenied} />;

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <Title level={3}>Unresolved Entities</Title>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: 80, gap: 16 }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />} />
          <Text type="secondary">Loading unresolved entities...</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Title level={3}>Unresolved Entities</Title>
        <Alert message="Failed to load unresolved entities" description={error} type="error" showIcon />
      </div>
    );
  }

  const allRows = data.rows;

  const filteredRows = allRows.filter((r) => {
    if (filters.application.length && !filters.application.includes(r.application_id)) return false;
    if (filters.entity_type.length && !filters.entity_type.includes(r.entity_type)) return false;
    return true;
  });

  // Re-aggregate counts from filtered rows for charts
  const appCounts = {};
  const typeCounts = {};
  for (const r of filteredRows) {
    appCounts[r.application_id] = (appCounts[r.application_id] || 0) + 1;
    typeCounts[r.entity_type] = (typeCounts[r.entity_type] || 0) + 1;
  }
  const byApp = Object.entries(appCounts)
    .map(([application_id, count]) => ({ application_id, count }))
    .sort((a, b) => b.count - a.count);
  const byType = Object.entries(typeCounts)
    .map(([entity_type, count]) => ({ entity_type, count }))
    .sort((a, b) => b.count - a.count);

  const columns = [
    {
      title: "Application ID",
      dataIndex: "application_id",
      key: "application_id",
      sorter: (a, b) => a.application_id.localeCompare(b.application_id),
      render: (v) => <span style={{ fontWeight: 500 }}>{v}</span>,
      width: 130,
    },
    {
      title: "Entity Type",
      dataIndex: "entity_type",
      key: "entity_type",
      filters: uniqueOptions(allRows, "entity_type").map(({ label, value }) => ({ text: label, value })),
      onFilter: (v, r) => r.entity_type === v,
      render: (v) => <Tag color={colorForType(v)}>{v}</Tag>,
      width: 160,
    },
    {
      title: "Entity Value",
      dataIndex: "entity_value",
      key: "entity_value",
      ellipsis: true,
    },
    {
      title: "Start Date",
      dataIndex: "start_measurement_date",
      key: "start_measurement_date",
      render: (v) => v ? v.split("T")[0] : "—",
      sorter: (a, b) => (a.start_measurement_date || "").localeCompare(b.start_measurement_date || ""),
      width: 110,
    },
    {
      title: "End Date",
      dataIndex: "end_measurement_date",
      key: "end_measurement_date",
      render: (v) => v ? v.split("T")[0] : "—",
      width: 110,
    },
  ];

  const hasFilters = filters.application.length > 0 || filters.entity_type.length > 0;

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Unresolved Entities</Title>

      {/* KPI */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card bordered={false} style={{ borderTop: `3px solid #f5222d` }}>
            <Statistic
              title="Total Unresolved"
              value={filteredRows.length}
              prefix={<WarningOutlined style={{ color: "#f5222d", marginRight: 4 }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card bordered={false} style={{ borderTop: `3px solid #fa8c16` }}>
            <Statistic title="Applications Affected" value={byApp.length} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card bordered={false} style={{ borderTop: `3px solid #faad14` }}>
            <Statistic title="Entity Types" value={byType.length} />
          </Card>
        </Col>
      </Row>

      {/* Filter bar */}
      <Card bordered={false} style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Select
              mode="multiple" allowClear showSearch placeholder="Application ID"
              style={{ width: "100%" }}
              value={filters.application}
              onChange={(v) => setFilters((f) => ({ ...f, application: v ?? [] }))}
              options={uniqueOptions(allRows, "application_id")}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              mode="multiple" allowClear showSearch placeholder="Entity Type"
              style={{ width: "100%" }}
              value={filters.entity_type}
              onChange={(v) => setFilters((f) => ({ ...f, entity_type: v ?? [] }))}
              options={uniqueOptions(allRows, "entity_type")}
            />
          </Col>
        </Row>
      </Card>

      {/* Charts */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={14}>
          <Card title="Unresolved Count by Application" bordered={false}>
            <ResponsiveContainer width="100%" height={Math.max(200, byApp.length * 28)}>
              <BarChart data={byApp} layout="vertical" margin={{ left: 20, right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="application_id" width={110} tick={{ fontSize: 12 }} />
                <ReTooltip formatter={(v) => [v, "Unresolved"]} />
                <Bar dataKey="count" fill="#f5222d" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title="Unresolved Count by Entity Type" bordered={false}>
            <ResponsiveContainer width="100%" height={Math.max(200, byType.length * 40)}>
              <BarChart data={byType} layout="vertical" margin={{ left: 20, right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="entity_type" width={140} tick={{ fontSize: 12 }} />
                <ReTooltip formatter={(v) => [v, "Unresolved"]} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {byType.map((entry) => (
                    <Cell key={entry.entity_type} fill={colorForType(entry.entity_type)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Detail table */}
      <Card title={`Entity Detail${hasFilters ? ` (${filteredRows.length} of ${allRows.length})` : ` (${allRows.length})`}`} bordered={false}>
        <Table
          dataSource={filteredRows.map((r, i) => ({ ...r, key: i }))}
          columns={columns}
          pagination={{
            pageSize,
            showSizeChanger: true,
            pageSizeOptions: ["20", "50", "100", "200"],
            onShowSizeChange: (_, size) => setPageSize(size),
          }}
          size="small"
          bordered
          scroll={{ x: true }}
        />
      </Card>
    </div>
  );
}

export default UnresolvedEntities;
